/**
 * @puffin-core/reserved-subdomains — canonical ecosystem reserved-subdomain SSOT (CJS).
 *
 * Subdomains on this list cannot resolve to a tenant and cannot be claimed at
 * signup. This is the UNION of every per-app reserved list that previously
 * lived across the ecosystem (the backend `SubdomainValidationService` lists,
 * the self-contained `companies.subdomain.js` routers, and the three frontend
 * client-side validators). Reserving a name is the safe direction (a reserved
 * name 403s on `/resolve` rather than leaking a platform host to a tenant), so
 * the union — never the intersection — is the correct merge.
 *
 * This package is DATA-ONLY and has ZERO runtime dependencies. It ships dual-use
 * CJS + ESM via the package.json `exports` map (`require` → this file, `import`
 * / `browser` → `./esm/index.js`). CJS consumers (the backend resolver) and ESM
 * consumers (the web validators) import the exact same frozen array. Drift
 * between this file and the ESM mirror is caught by
 * `tests/esm/native-import.test.mjs`.
 *
 * IMPORTANT (deployment safety): because every `/resolve` handler gates on this
 * list BEFORE the tenant lookup, adding a name here will start returning 403 for
 * any LIVE tenant whose subdomain equals that name. Before shipping a change to
 * this list, confirm no active `public.companies.subdomain` value collides with
 * a newly-added entry.
 *
 * @module @puffin-core/reserved-subdomains
 */

'use strict';

/**
 * The canonical reserved-subdomain list, frozen so consumers cannot mutate the
 * SSOT. Grouped by intent for readability; group boundaries carry no behavior.
 *
 * @type {readonly string[]}
 */
const RESERVED_SUBDOMAINS = Object.freeze([
  // Infrastructure
  'www',
  'api',
  'app',
  'admin',
  'dashboard',
  'mail',
  'smtp',
  'imap',
  'pop',
  'ftp',
  'sftp',
  'cdn',
  'static',
  'assets',
  'media',
  'files',

  // Development / Testing
  'localhost',
  'local',
  'dev',
  'development',
  'staging',
  'stage',
  'test',
  'testing',
  'qa',
  'sandbox',
  'demo',
  'preview',
  'beta',
  'alpha',

  // Marketing / Business
  'blog',
  'news',
  'support',
  'help',
  'docs',
  'status',
  'jobs',
  'careers',
  'about',
  'contact',
  'legal',
  'privacy',
  'terms',
  'security',

  // Technical / DNS
  'ns1',
  'ns2',
  'dns',
  'mx',
  'autodiscover',
  'webmail',
  'cpanel',
  'whm',
  'plesk',

  // Authentication
  'login',
  'logout',
  'auth',
  'oauth',
  'sso',
  'signin',
  'signout',
  'signup',
  'register',

  // Platform
  'console',
  'platform',
  'internal',
  'system',
  'root',
  'null',
  'undefined',
  'administrator',
]);

/**
 * The resolve endpoint's DEFINITIVE refusals: each code a `GET /companies/resolve` answers when a subdomain names no
 * tenant a person may use, with the status it is answered with (CLIENT-RETRY-IDEMPOTENCY-001 U3 #4; THINKPAD, note
 * 20261030T0319Z).
 *
 * Declared ONCE for both sides: `@puffin-core/subdomain-resolver`'s factory answers from it, and
 * `@puffin-core/subdomain-routing`'s resolver reads "no such store" from EXACTLY these codes. Any other non-OK answer
 * (a WAF's uncoded 403, a missing route's 404, a 501, our own coded 503) is a FAULT, never a miss: the store exists and
 * the sign-in says it could not be reached.
 *
 * @type {Readonly<Record<string, number>>}
 */
const SUBDOMAIN_RESOLVE_REFUSALS = Object.freeze({
  // The route's ONE validator answers a missing or malformed subdomain with its value-free 422 (`resolveQuerySchema`,
  // ruling (b)): a subdomain that cannot be a store's is a definitive miss, never a fault.
  VALIDATION_ERROR: 422,
  SUBDOMAIN_NOT_FOUND: 404,
  COMPANY_SUSPENDED: 403,
  COMPANY_CANCELLED: 403,
  SUBDOMAIN_REQUIRED: 400,
  SUBDOMAIN_RESERVED: 403,
});

module.exports = { RESERVED_SUBDOMAINS, SUBDOMAIN_RESOLVE_REFUSALS };
